import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, ScanLine, Package, X } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useToast } from "@/hooks/use-toast";
import { useSpareParts } from "@/hooks/useSpareParts";
import { isInStock } from "@/lib/partStatus";
import { useResalePricePct } from "@/hooks/useResalePricePct";
import { formatEuro, harvestedThisMonthValue, stockValue, sumPartValues } from "@/lib/partValue";
import { useAllStorageBins, useStorageLocations } from "@/hooks/useStorageBins";
import { binLevelEntries, compareBinLevels } from "@/lib/binLevels";
import { BinScanner } from "@/components/BinScanner";
import { BinLocationChoiceDialog, type BinLocationChoice } from "@/components/BinLocationChoiceDialog";
import { resolveScannedBin } from "@/lib/binScan";
import { groupPartTypes } from "@/lib/partTypes";
import { useCompanyApplianceCategories } from "@/hooks/useCompanyApplianceCategories";
import { InventorySortMenu, type SortFieldDef, type SortEntry } from "@/components/InventorySortMenu";

type Tab = "parts" | "bins";
type PartsSortKey = "stock" | "name" | "recent";
type BinsSortKey = "name" | "warehouse" | "parts";
type StockStatus = "all" | "in_stock" | "awaiting";

/** Sentinel value for parts with no configured parts category. */
const UNCATEGORISED = "__uncategorised";

const PARTS_SORT_FIELDS: readonly SortFieldDef<PartsSortKey>[] = [
  { key: "stock", label: "Stock", directions: { desc: "Highest → Lowest", asc: "Lowest → Highest" }, defaultDir: "desc" },
  { key: "name", label: "Name", directions: { asc: "A → Z", desc: "Z → A" }, defaultDir: "asc" },
  { key: "recent", label: "Added", directions: { desc: "Newest → Oldest", asc: "Oldest → Newest" }, defaultDir: "desc" },
];
const BINS_SORT_FIELDS: readonly SortFieldDef<BinsSortKey>[] = [
  { key: "name", label: "Bin code", directions: { asc: "A → Z", desc: "Z → A" }, defaultDir: "asc" },
  { key: "warehouse", label: "Warehouse", directions: { asc: "A → Z", desc: "Z → A" }, defaultDir: "asc" },
  { key: "parts", label: "Parts", directions: { desc: "Highest → Lowest", asc: "Lowest → Highest" }, defaultDir: "desc" },
];
const PARTS_DEFAULT_SORTS: SortEntry<PartsSortKey>[] = [{ key: "stock", dir: "desc" }];
const BINS_DEFAULT_SORTS: SortEntry<BinsSortKey>[] = [{ key: "name", dir: "asc" }];

const BIN_LEVEL_LABELS = { aisle: "Aisle", bay: "Bay", level: "Level", shelf: "Shelf", bin: "Bin" } as const;

export default function Inventory() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("inventory");
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: Tab = searchParams.get("tab") === "bins" ? "bins" : "parts";

  const { data: spareParts = [] } = useSpareParts();
  const { data: bins = [] } = useAllStorageBins();
  const { data: locations = [] } = useStorageLocations();
  const { names: applianceCategoryNames } = useCompanyApplianceCategories();

  const [query, setQuery] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  // Parts filters
  const [applianceFilter, setApplianceFilter] = useState<string>("all");
  const [partCategoryFilter, setPartCategoryFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [brandFilter, setBrandFilter] = useState<string>("all");
  const [modelFilter, setModelFilter] = useState<string>("all");
  const [binFilter, setBinFilter] = useState<string>("all");
  const [stockFilter, setStockFilter] = useState<StockStatus>("all");
  const [partsSorts, setPartsSorts] = useState<SortEntry<PartsSortKey>[]>(PARTS_DEFAULT_SORTS);
  const [binsSorts, setBinsSorts] = useState<SortEntry<BinsSortKey>[]>(BINS_DEFAULT_SORTS);

  useEffect(() => {
    searchRef.current?.focus();
  }, [tab]);

  const setTab = (next: Tab) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", next);
    setSearchParams(params, { replace: true });
  };

  const binById = useMemo(() => new Map(bins.map((b) => [b.id, b])), [bins]);
  // An old bin label whose code exists in two locations: ask which one (FIX-319).
  const [binChoice, setBinChoice] = useState<BinLocationChoice | null>(null);
  const locationById = useMemo(
    () => new Map(locations.map((l) => [l.id, l])),
    [locations],
  );

  // FIX-274: stock views count in-stock parts only; tryUnitJump still searches all parts.
  const stockParts = useMemo(() => spareParts.filter((p) => isInStock(p.status)), [spareParts]);

  // FIX-276: € figures, same per-part value as the part page.
  const resalePct = useResalePricePct();
  const euro = (v: number) => formatEuro(v, i18n.language);
  const totalStockValue = useMemo(() => stockValue(spareParts, resalePct), [spareParts, resalePct]);
  const monthValue = useMemo(() => harvestedThisMonthValue(spareParts, resalePct), [spareParts, resalePct]);

  const partsPerBin = useMemo(() => {
    const m = new Map<string, number>();
    stockParts.forEach((p) => {
      if (p.bin_id) m.set(p.bin_id, (m.get(p.bin_id) || 0) + 1);
    });
    return m;
  }, [stockParts]);

  const allPartTypes = useMemo(() => groupPartTypes(stockParts), [stockParts]);

  const tokens = useMemo(
    () => query.trim().toLowerCase().split(/\s+/).filter(Boolean),
    [query],
  );
  const matchTokens = (haystack: string) => tokens.every((t) => haystack.includes(t));

  type PartTypeRow = (typeof allPartTypes)[number];
  type FacetKey =
    | "appliance"
    | "partCategory"
    | "type"
    | "brand"
    | "model"
    | "bin"
    | "stock";

  const haystackFor = (p: PartTypeRow) =>
    [
      p.name,
      p.brand,
      p.oem_part_number,
      ...p.compatible_models,
      ...p.units.map(
        (u) => `${u.serial_no ?? ""} ${u.qr_token} ${u.appliance_serial_number ?? ""}`,
      ),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

  const predicates = useMemo(() => {
    const map: Record<FacetKey, (p: PartTypeRow) => boolean> = {
      appliance: (p) =>
        applianceFilter === "all" ||
        p.units.some(
          (u) =>
            (u.appliance_category || "").toLowerCase() === applianceFilter.toLowerCase(),
        ),
      partCategory: (p) =>
        partCategoryFilter === "all" ||
        (partCategoryFilter === UNCATEGORISED
          ? p.parts_category_names.length === 0
          : p.parts_category_names.some(
              (c) => c.toLowerCase() === partCategoryFilter.toLowerCase(),
            )),
      type: (p) => typeFilter === "all" || p.name === typeFilter,
      brand: (p) => brandFilter === "all" || p.brand === brandFilter,
      model: (p) => modelFilter === "all" || p.compatible_models.includes(modelFilter),
      bin: (p) => binFilter === "all" || p.units.some((u) => u.bin_id === binFilter),
      stock: (p) => {
        if (stockFilter === "in_stock") return p.stock - p.awaiting > 0;
        if (stockFilter === "awaiting") return p.awaiting > 0;
        return true;
      },
    };
    return map;
  }, [
    applianceFilter,
    partCategoryFilter,
    typeFilter,
    brandFilter,
    modelFilter,
    binFilter,
    stockFilter,
  ]);

  /** Part types passing every filter except the given facet (text search always applies). */
  const rowsExcluding = useMemo(() => {
    const keys = Object.keys(predicates) as FacetKey[];
    const base = allPartTypes.filter((p) => !tokens.length || matchTokens(haystackFor(p)));
    const result = {} as Record<FacetKey | "__all", PartTypeRow[]>;
    for (const skip of keys) {
      result[skip] = base.filter((p) =>
        keys.every((k) => k === skip || predicates[k](p)),
      );
    }
    result.__all = base.filter((p) => keys.every((k) => predicates[k](p)));
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPartTypes, predicates, query]);

  const countLabel = (n: number) => `${n} ${n === 1 ? "item" : "items"}`;

  /** Build facet options from counted values, hiding zero-result entries. */
  const buildOptions = (
    counts: Map<string, number>,
    allLabel: string,
    labelFor?: (value: string) => string,
    sortValues?: (a: string, b: string) => number,
  ) => {
    const total = Array.from(counts.values()).reduce((a, b) => a + b, 0);
    const values = Array.from(counts.keys())
      .filter((v) => (counts.get(v) || 0) > 0)
      .sort(sortValues ?? ((a, b) => a.localeCompare(b)));
    return [
      { value: "all", label: allLabel, description: countLabel(total) },
      ...values.map((v) => ({
        value: v,
        label: labelFor ? labelFor(v) : v,
        description: countLabel(counts.get(v) || 0),
      })),
    ];
  };

  const applianceOptions = useMemo(() => {
    const allowed = new Set(applianceCategoryNames.map((n) => n.toLowerCase()));
    const counts = new Map<string, number>();
    const canonical = new Map<string, string>();
    applianceCategoryNames.forEach((n) => canonical.set(n.toLowerCase(), n));
    rowsExcluding.appliance.forEach((p) => {
      const seen = new Set<string>();
      p.units.forEach((u) => {
        const cat = (u.appliance_category || "").trim();
        if (!cat) return;
        const key = cat.toLowerCase();
        if (allowed.size > 0 && !allowed.has(key)) return;
        if (seen.has(key)) return;
        seen.add(key);
        const name = canonical.get(key) ?? cat;
        counts.set(name, (counts.get(name) || 0) + p.stock);
      });
    });
    return buildOptions(counts, "All Appliances");
  }, [rowsExcluding, applianceCategoryNames]);

  const partCategoryOptions = useMemo(() => {
    const counts = new Map<string, number>();
    rowsExcluding.partCategory.forEach((p) => {
      if (p.parts_category_names.length === 0) {
        counts.set(UNCATEGORISED, (counts.get(UNCATEGORISED) || 0) + p.stock);
        return;
      }
      // A group's units can resolve to different categories; count it in each.
      p.parts_category_names.forEach((c) => {
        counts.set(c, (counts.get(c) || 0) + p.stock);
      });
    });
    return buildOptions(
      counts,
      "All Parts Categories",
      (v) => (v === UNCATEGORISED ? "Uncategorised" : v),
      (a, b) =>
        a === UNCATEGORISED ? 1 : b === UNCATEGORISED ? -1 : a.localeCompare(b),
    );
  }, [rowsExcluding]);

  const typeOptions = useMemo(() => {
    const counts = new Map<string, number>();
    rowsExcluding.type.forEach((p) => {
      counts.set(p.name, (counts.get(p.name) || 0) + p.stock);
    });
    return buildOptions(counts, "All item types");
  }, [rowsExcluding]);

  const brandOptions = useMemo(() => {
    const counts = new Map<string, number>();
    rowsExcluding.brand.forEach((p) => {
      if (!p.brand) return;
      counts.set(p.brand, (counts.get(p.brand) || 0) + p.stock);
    });
    return buildOptions(counts, "All brands");
  }, [rowsExcluding]);

  const modelOptions = useMemo(() => {
    const counts = new Map<string, number>();
    rowsExcluding.model.forEach((p) => {
      p.compatible_models.forEach((m) => counts.set(m, (counts.get(m) || 0) + p.stock));
    });
    return buildOptions(counts, "All models");
  }, [rowsExcluding]);

  const binFilterOptions = useMemo(() => {
    const counts = new Map<string, number>();
    rowsExcluding.bin.forEach((p) => {
      const perBin = new Map<string, number>();
      p.units.forEach((u) => {
        if (!u.bin_id) return;
        perBin.set(u.bin_id, (perBin.get(u.bin_id) || 0) + 1);
      });
      perBin.forEach((n, id) => counts.set(id, (counts.get(id) || 0) + n));
    });
    return buildOptions(
      counts,
      "All bins",
      (id) => binById.get(id)?.address ?? id,
      (a, b) =>
        (binById.get(a)?.address ?? a).localeCompare(binById.get(b)?.address ?? b),
    );
  }, [rowsExcluding, binById]);

  const stockOptions = useMemo(() => {
    const rows = rowsExcluding.stock;
    const sum = (fn: (p: PartTypeRow) => boolean) =>
      rows.filter(fn).reduce((acc, p) => acc + p.stock, 0);
    return {
      all: sum(() => true),
      in_stock: sum((p) => p.stock - p.awaiting > 0),
      awaiting: sum((p) => p.awaiting > 0),
    };
  }, [rowsExcluding]);

  // Reset any selection that is no longer offered by its facet.
  useEffect(() => {
    const check = (
      value: string,
      options: { value: string }[],
      reset: (v: string) => void,
    ) => {
      if (value !== "all" && !options.some((o) => o.value === value)) reset("all");
    };
    check(applianceFilter, applianceOptions, setApplianceFilter);
    check(partCategoryFilter, partCategoryOptions, setPartCategoryFilter);
    check(typeFilter, typeOptions, setTypeFilter);
    check(brandFilter, brandOptions, setBrandFilter);
    check(modelFilter, modelOptions, setModelFilter);
    check(binFilter, binFilterOptions, setBinFilter);
  }, [
    applianceOptions,
    partCategoryOptions,
    typeOptions,
    brandOptions,
    modelOptions,
    binFilterOptions,
    applianceFilter,
    partCategoryFilter,
    typeFilter,
    brandFilter,
    modelFilter,
    binFilter,
  ]);

  // ---------- Part types ----------
  const filteredPartTypes = useMemo(() => {
    const rows = [...rowsExcluding.__all];
    const cmp = (a: PartTypeRow, b: PartTypeRow, k: PartsSortKey) => {
      if (k === "name") return a.name.localeCompare(b.name);
      if (k === "recent") return b.latest_harvested_at.localeCompare(a.latest_harvested_at);
      return b.stock - a.stock;
    };
    return rows.sort((a, b) => {
      for (const s of partsSorts) {
        const v = cmp(a, b, s.key);
        if (v !== 0) return s.dir === "asc" ? -v : v;
      }
      return 0;
    });
  }, [rowsExcluding, partsSorts]);

  const filteredUnitCount = useMemo(
    () => filteredPartTypes.reduce((acc, p) => acc + p.stock, 0),
    [filteredPartTypes],
  );


  

  const clearFilters = () => {
    setApplianceFilter("all");
    setPartCategoryFilter("all");
    setTypeFilter("all");
    setBrandFilter("all");
    setModelFilter("all");
    setBinFilter("all");
    setStockFilter("all");
    setQuery("");
    searchRef.current?.focus();
  };

  const filtersActive =
    applianceFilter !== "all" ||
    partCategoryFilter !== "all" ||
    typeFilter !== "all" ||
    brandFilter !== "all" ||
    modelFilter !== "all" ||
    binFilter !== "all" ||
    stockFilter !== "all" ||
    query.length > 0;

  // ---------- Bins ----------
  const filteredBins = useMemo(() => {
    const rows = bins.filter((b) => {
      if (!tokens.length) return true;
      const loc = locationById.get(b.location_id)?.name || "";
      return matchTokens(`${b.address} ${loc}`.toLowerCase());
    });
    return rows.sort((a, b) => {
      for (const s of binsSorts) {
        let v = 0;
        if (s.key === "name")
          v = compareBinLevels(a, b);
        else if (s.key === "warehouse")
          v = (locationById.get(a.location_id)?.name || "").localeCompare(
            locationById.get(b.location_id)?.name || "",
          );
        else if (s.key === "parts")
          v = (partsPerBin.get(b.id) || 0) - (partsPerBin.get(a.id) || 0);
        if (v !== 0) return s.dir === "asc" ? -v : v;
      }
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bins, query, locationById, partsPerBin, binsSorts]);

  // ---------- Scanner / unit jump ----------
  const tryUnitJump = (raw: string): boolean => {
    const text = raw.trim();
    if (!text) return false;
    // URL form
    try {
      const url = new URL(text);
      const m = url.pathname.match(/\/spare-part\/([^/?#]+)/i);
      if (m) {
        navigate(`/spare-part/${decodeURIComponent(m[1])}`);
        return true;
      }
      const mb = url.pathname.match(/\/bin\/([^/?#]+)/i);
      if (mb) {
        navigate(`/bin/${decodeURIComponent(mb[1])}`);
        return true;
      }
    } catch {
      /* not a URL */
    }

    // Bin label: by id on new labels, by code on old ones (FIX-319)
    const binScan = resolveScannedBin(text, bins);
    if (binScan.kind === "bin") {
      navigate(`/bin/${binScan.bin.id}`);
      return true;
    }
    if (binScan.kind === "choose") {
      setBinChoice({ bins: binScan.bins, onPick: (b) => navigate(`/bin/${b.id}`) });
      return true;
    }
    const part = spareParts.find(
      (p) =>
        p.serial_no?.toUpperCase() === text.toUpperCase() ||
        p.qr_token?.toUpperCase() === text.toUpperCase(),
    );
    if (part) {
      navigate(`/spare-part/${part.serial_no || part.qr_token}`);
      return true;
    }
    return false;
  };

  const handleScan = (raw: string) => {
    if (!tryUnitJump(raw)) {
      toast({
        title: "No match",
        description: `No matching part or bin for "${raw.trim()}".`,
        variant: "destructive",
      });
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && query.trim()) {
      tryUnitJump(query);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-1">Inventory</h1>
            <p className="text-muted-foreground">
              {tab === "parts"
                ? "Browse for spare parts in your inventory."
                : "Browse bins and what's stored in them."}
            </p>
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1">
              <div>
                <div className="text-xs text-muted-foreground">{t("value.stock")}</div>
                <div className="text-lg font-semibold text-foreground">{euro(totalStockValue)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">{t("value.harvestedThisMonth")}</div>
                <div className="text-lg font-semibold text-foreground">{euro(monthValue)}</div>
              </div>
            </div>
          </div>
          <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
            <TabsList>
              <TabsTrigger value="parts">Parts · {allPartTypes.length}</TabsTrigger>
              <TabsTrigger value="bins">Bins · {bins.length}</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Search */}
      <div className="px-6 mb-4">
        <div
          className="flex items-center gap-3 bg-card rounded-xl px-4 py-2.5 border-[1.5px] border-primary/60"
          style={{ boxShadow: "0 0 0 4px hsl(var(--primary) / 0.15)" }}
        >
          <Search className="w-5 h-5 text-primary shrink-0" />
          <input
            ref={searchRef}
            autoFocus
            type="text"
            placeholder={
              tab === "parts"
                ? "Search by name, part number, model, label, serial…"
                : "Search by bin code or location…"
            }
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
            aria-label="Search inventory"
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery("");
                searchRef.current?.focus();
              }}
              className="text-muted-foreground hover:text-foreground shrink-0"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setScannerOpen(true)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium shrink-0 hover:bg-primary/20 transition-colors"
            aria-label="Scan"
            title="Scan"
          >
            <ScanLine className="w-4 h-4" />
            Scan
          </button>
        </div>
      </div>

      {/* Parts tab */}
      {tab === "parts" && (
        <>
          <div className="px-6 mb-4 flex flex-wrap items-center gap-2">
            <SearchableSelect
              className="w-[200px]"
              value={applianceFilter}
              onChange={(v) => setApplianceFilter(v ?? "all")}
              placeholder="All Appliances"
              searchPlaceholder="Search appliances…"
              emptyMessage="No appliance categories"
              disabled={applianceOptions.length <= 1}
              items={applianceOptions}
            />

            <SearchableSelect
              className="w-[200px]"
              value={partCategoryFilter}
              onChange={(v) => setPartCategoryFilter(v ?? "all")}
              placeholder="All Parts Categories"
              searchPlaceholder="Search categories…"
              emptyMessage="No parts categories"
              disabled={partCategoryOptions.length <= 1}
              items={partCategoryOptions}
            />

            <SearchableSelect
              className="w-[180px]"
              value={typeFilter}
              onChange={(v) => setTypeFilter(v ?? "all")}
              placeholder="Item type"
              searchPlaceholder="Search item types…"
              emptyMessage="No item types"
              disabled={typeOptions.length <= 1}
              items={typeOptions}
            />

            <SearchableSelect
              className="w-[160px]"
              value={brandFilter}
              onChange={(v) => setBrandFilter(v ?? "all")}
              placeholder="Brand"
              searchPlaceholder="Search brands…"
              emptyMessage="No brands"
              disabled={brandOptions.length <= 1}
              items={brandOptions}
            />

            <SearchableSelect
              className="w-[200px]"
              value={modelFilter}
              onChange={(v) => setModelFilter(v ?? "all")}
              placeholder="Compatible model"
              searchPlaceholder="Search models…"
              emptyMessage="No models"
              disabled={modelOptions.length <= 1}
              items={modelOptions}
            />

            <SearchableSelect
              className="w-[180px]"
              value={binFilter}
              onChange={(v) => setBinFilter(v ?? "all")}
              placeholder="Bin"
              searchPlaceholder="Search bins…"
              emptyMessage="No bins"
              disabled={binFilterOptions.length <= 1}
              items={binFilterOptions}
            />


            <Select value={stockFilter} onValueChange={(v) => setStockFilter(v as StockStatus)}>
              <SelectTrigger className="w-[170px]"><SelectValue placeholder={t("status.stockStatus")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("status.allStock")} · {stockOptions.all}</SelectItem>
                <SelectItem value="in_stock">{t("status.inStock")} · {stockOptions.in_stock}</SelectItem>
                <SelectItem value="awaiting">{t("status.notInBin")} · {stockOptions.awaiting}</SelectItem>
              </SelectContent>
            </Select>


            <div className="ml-auto">
              {filtersActive && (
                <button onClick={clearFilters} className="text-sm text-primary hover:underline">
                  Clear filters
                </button>
              )}
            </div>
          </div>

          <div className="px-6 mb-3 flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              {filteredPartTypes.length} part {filteredPartTypes.length === 1 ? "type" : "types"} · {filteredUnitCount} {filteredUnitCount === 1 ? "part" : "parts"}
            </p>
            <InventorySortMenu
              entityLabel="parts"
              fields={PARTS_SORT_FIELDS}
              value={partsSorts}
              defaultValue={PARTS_DEFAULT_SORTS}
              onChange={setPartsSorts}
            />
          </div>


          <div className="px-6">
            {filteredPartTypes.length === 0 ? (
              <div className="bg-card rounded-xl border border-border p-10 text-center">
                <Package className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-foreground font-medium mb-1">Nothing in inventory yet.</p>
                <p className="text-sm text-muted-foreground">
                  Start a harvest run from Work Orders.
                </p>
              </div>
            ) : (
              <ul className="space-y-2">
                {filteredPartTypes.map((p) => {
                  const firstModel = p.compatible_models[0];
                  const extra = Math.max(0, p.compatible_models.length - 1);
                  const rowValue = sumPartValues(p.units, resalePct);
                  return (
                    <li key={p.id}>
                      <button
                        onClick={() => navigate(`/inventory/parts/${p.id}`)}
                        className="w-full text-left bg-card rounded-xl border border-border p-3 flex items-center gap-3 transition-all hover:border-primary hover:shadow-md"
                      >
                        <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center shrink-0">
                          <Package className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-foreground truncate">{p.name}</div>
                          <div className="text-xs font-mono text-muted-foreground truncate">
                            {p.oem_part_number || "—"}
                            {p.brand && ` · ${p.brand}`}
                          </div>
                          {firstModel && (
                            <div className="text-xs text-muted-foreground truncate mt-0.5">
                              fits {firstModel}{extra > 0 ? ` +${extra} models` : ""}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <span className="text-base font-semibold text-foreground">
                            {t("status.countInStock", { count: p.stock })}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {rowValue == null ? "—" : euro(rowValue)}
                          </span>
                          {p.awaiting > 0 && (
                            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">
                              {t("status.countNotInBin", { count: p.awaiting })}
                            </span>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}

      {/* Bins tab */}
      {tab === "bins" && (
        <div className="px-6">
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              {filteredBins.length} {filteredBins.length === 1 ? "bin" : "bins"}
            </p>
            <InventorySortMenu
              entityLabel="bins"
              fields={BINS_SORT_FIELDS}
              value={binsSorts}
              defaultValue={BINS_DEFAULT_SORTS}
              onChange={setBinsSorts}
            />
          </div>

          {filteredBins.length === 0 ? (
            <div className="bg-card rounded-xl border border-border p-8 text-center text-muted-foreground">
              No bins match your search.
            </div>
          ) : (
            <ul className="space-y-2">
              {filteredBins.map((b) => {
                const loc = locationById.get(b.location_id);
                const count = partsPerBin.get(b.id) || 0;
                return (
                  <li key={b.id}>
                    <button
                      onClick={() => navigate(`/bin/${b.id}`)}
                      className="w-full text-left bg-card rounded-xl border border-border p-3 flex items-center gap-3 transition-all hover:border-primary hover:shadow-md"
                    >
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-primary/10 text-primary font-mono text-sm shrink-0">
                        {b.address}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-muted-foreground truncate">
                          {loc?.name || "Storage location"}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {/* Only the levels this location uses (FIX-318). */}
                          {binLevelEntries(b)
                            .map(({ key, value }) => `${BIN_LEVEL_LABELS[key]} ${value}`)
                            .join(" · ")}
                        </div>
                      </div>
                      <span className="px-2 py-1 rounded-md bg-muted text-muted-foreground text-xs shrink-0">
                        {count} {count === 1 ? "part" : "parts"}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      <BinScanner
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onDetected={handleScan}
      />
      <BinLocationChoiceDialog choice={binChoice} onClose={() => setBinChoice(null)} />
    </div>
  );
}
